import type { AutocompletePrediction } from "@/modules/google/types"
import type { PlaceResultFailedCallback } from "@/modules/jobAds/admin/components/InputLocation/types/placeResultFailedCallback"
import type { PlaceResultSucceededCallback } from "@/modules/jobAds/admin/components/InputLocation/types/placeResultSucceededCallback"
import type { PlacesServiceDetailsCallback } from "@/modules/jobAds/admin/components/InputLocation/types/placesServiceDetailsCallback"
import type { SimpleSelectOptions } from "@/modules/uiKitOverrides/SelectFilter/Select"

type LocationType = "country" | "state" | "subState" | "city"

export type Location = {
  city?: string
  country?: string
  formattedAddress?: string
  latitude?: number
  localizedLocation?: string
  longitude?: number
  placeId?: string
  state?: string
  subState?: string
}

export type LocationForProto = {
  city?: string
  country?: string
  state?: string
  sub_state?: string
  woosmap_place_id?: string
  google_place_id?: string
  country_code?: string
}

const locationToPlaceAttribute = {
  city: "locality",
  country: "country",
  state: "administrative_area_level_1",
  subState: "administrative_area_level_2",
}

type LocationToPlaceAttribute = typeof locationToPlaceAttribute

export const formatPredictionsToOptions = (predictions: AutocompletePrediction[] | null): SimpleSelectOptions[] => {
  if (!predictions || predictions.length === 0) return []

  return predictions.map(prediction => ({
    label: prediction.description,
    value: prediction.place_id,
  }))
}

export const setLocationFromPlaceResult =
  (
    succeededCallback: PlaceResultSucceededCallback,
    failedCallback: PlaceResultFailedCallback
  ): PlacesServiceDetailsCallback =>
  (placeResult, placeServiceStatus): void => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    if (placeServiceStatus !== "OK" || !placeResult || !placeResult.address_components) {
      failedCallback(placeServiceStatus)

      return
    }

    const location = placeResult.address_components.reduce((locationObject, component): LocationToPlaceAttribute => {
      const { types } = component

      for (const [locationKey, placeType] of Object.entries(locationToPlaceAttribute)) {
        if (types.includes(placeType)) {
          return {
            ...locationObject,
            [locationKey]: component.long_name,
          }
        }
      }

      return locationObject
    }, {} as LocationToPlaceAttribute)

    succeededCallback({
      city: location.city ?? "",
      country: location.country ?? "",
      formattedAddress: placeResult.formatted_address ?? "",
      latitude: placeResult?.geometry?.location?.lat() ?? 0,
      longitude: placeResult?.geometry?.location?.lng() ?? 0,
      placeId: placeResult.place_id ?? "",
      state: location.state ?? "",
      subState: location.subState ?? "",
    })
  }

const getLocationLabelFromLegacy = (legacyLocationLabel?: string): string | null =>
  legacyLocationLabel ? legacyLocationLabel.split("..")[0] : null

export const getDisplayableLocation = (location: Location | null | undefined, isWithCountry = true): string | null => {
  if (!location) {
    return null
  }

  if (location?.localizedLocation) {
    return location.localizedLocation
  }

  let result = null
  const city = getLocationLabelFromLegacy(location.city)
  const subState = getLocationLabelFromLegacy(location.subState)
  const state = getLocationLabelFromLegacy(location.state)
  const country = getLocationLabelFromLegacy(location.country)

  if (city && city.length > 0) {
    result = city
  } else if (subState && subState.length > 0) {
    result = subState
  } else if (state && state.length > 0) {
    result = state
  }

  if (country && country.length > 0) {
    result = result && result.length > 0 ? `${result}${isWithCountry ? `, ${country}` : ""}` : country
  }

  return result
}

export const computeLocationQueryParamFromObject = (location: Location): string => {
  const state = location?.state || ""
  const subState = location?.subState || ""
  const city = location?.city || ""
  const placeId = location?.placeId || ""

  let request = location?.country || ""

  /**
   * We used the same algorithm as on the legacy so as not to introduce a breaking change between next and the legacy search (besides, this algorithm is subject to change with Algolia)
   * FYI, the old algorithm : https://github.com/jobteaser/ui-jobteaser/blob/9c9ab7504556808706347a7feac0361bf4afa65f/web_app/src/shared_spa/utils/helper/index.js#L190
   */
  if (city) {
    request += `::${state}::${subState}::${city}`
  } else if (subState) {
    request += `::${state}::${subState}`
  } else if (state) {
    request += `::${state}`
  }

  if (placeId) request += `::_${placeId}` // only here to retrieve placeId on reload (without re-calling API)

  return request
}

export const getLocationType = (locationObject: Location): LocationType => {
  const { country, state, subState, city } = locationObject

  const hasCountry = country && !state && !subState && !city
  const hasState = country && state && !subState && !city
  const hasSubState = country && state && subState && !city

  if (hasCountry) return "country"
  if (hasState) return "state"
  if (hasSubState) return "subState"

  return "city"
}

export const computeLocationObjectFromSingleQueryParam = (
  locationQueryParam: string,
  localizedLocation: string | null = ""
): Location => {
  const locationArray = locationQueryParam.split("::")
  const placeIdIndex = locationArray.findIndex(item => item.startsWith("_"))

  const country = locationArray[0]
  const state = placeIdIndex !== 1 ? locationArray[1] : null
  const subState = placeIdIndex !== 2 ? locationArray[2] : null
  const city = placeIdIndex !== 3 ? locationArray[3] : null
  const placeId = locationArray[placeIdIndex]

  return {
    ...(country ? { country } : {}),
    ...(state ? { state } : {}),
    ...(subState ? { subState } : {}),
    ...(city ? { city } : {}),
    ...(placeId ? { placeId: placeId.replace("_", "") } : {}),
    ...(localizedLocation && { localizedLocation }),
  }
}
